'use client'

import { useI18n } from '@shared/config/i18n'
import { UI_IDS } from '@shared/constants'
import { formatKrwAmount } from '@shared/utils'
import { useState } from 'react'

import { Button, Flex, HStack, Typography, VStack } from '@podo-app/design-system-temp'
import { ChevronDownIcon } from '@podo/assets'

type PaymentType = 'lump-sum' | 'subscribe' | 'ipad'

export type DiscountItem = {
  label: string
  amount: number
}

type Props = {
  type: PaymentType
  title: string
  originalPrice: number
  /** 기본 할인가 (회색 박스 밖에 표시) */
  viewPrice: number
  /** 기본 할인율 (회색 박스 밖에 표시) */
  viewDiscountRate: number
  /** 최종 할인가 (쿠폰 적용, 회색 박스 안에 표시) */
  finalPrice: number
  /** 최종 할인율 (쿠폰 적용, 회색 박스 안에 표시) */
  finalDiscountRate: number
  installmentMonths?: number
  savingsAmount?: number
  discountDetails?: DiscountItem[]
  onPurchase: () => void
  isPurchasing?: boolean
  'data-testid'?: string
}

const PaymentOptionCard = ({
  type,
  title,
  originalPrice,
  viewPrice,
  viewDiscountRate,
  finalPrice,
  finalDiscountRate,
  installmentMonths,
  savingsAmount,
  discountDetails = [],
  onPurchase,
  isPurchasing = false,
  'data-testid': testId,
}: Props) => {
  const { effectiveLocale, t } = useI18n()
  const isLumpSum = type === 'lump-sum'
  const isIpad = type === 'ipad'
  const [isExpanded, setIsExpanded] = useState(false)

  const hasDiscountDetails = discountDetails.length > 0
  // 쿠폰 적용 여부: discountDetails가 있거나 viewPrice와 finalPrice가 다르면 쿠폰 적용됨
  const hasCouponApplied = hasDiscountDetails || viewPrice !== finalPrice

  return (
    <VStack className="relative bg-white border border-[#E8E8E8] rounded-xl p-5 gap-5" data-testid={testId}>
      {isLumpSum && savingsAmount && savingsAmount > 0 && (
        <div
          className="absolute right-0 top-[-1px] flex items-center whitespace-nowrap rounded-bl rounded-tl rounded-tr-xl px-2 py-px h-5"
          style={{ backgroundColor: '#1C1C1C' }}
        >
          <span className="text-[10px] font-semibold text-white">
            {t('subscribeList.paymentOption.saving', {
              amount: formatKrwAmount(savingsAmount, effectiveLocale),
            })}
          </span>
        </div>
      )}

      {/* 타이틀 + 무이자 할부 정보 */}
      <VStack className="gap-2">
        <VStack className="gap-0.5">
          <Typography size="h3" weight="bold" className="text-[#1C1C1C]">
            {title}
          </Typography>
          {installmentMonths && (
            <span className="text-[12px] text-[#6184FF] tracking-[-0.36px] leading-[18px]">
              {t('subscribeList.paymentOption.installment', { months: installmentMonths })}
            </span>
          )}
        </VStack>

        {/* 가격 정보 */}
        <VStack>
          {/* 원가 (취소선) */}
          <span className="text-[14px] text-gray-400 line-through">
            {formatKrwAmount(originalPrice, effectiveLocale)}
            {t('subscribeList.ticket.perMonth')}
          </span>

          {hasCouponApplied ? (
            // 쿠폰 적용됨: 기본 할인가 (회색) + 최대 혜택 적용가 박스
            <HStack className="gap-0.5 items-center">
              <span className="text-[14px] font-bold text-gray-400">{viewDiscountRate}%</span>
              <span className="text-[14px] font-bold text-gray-400">{formatKrwAmount(viewPrice, effectiveLocale)}</span>
              <span className="text-[14px] text-gray-400">{t('subscribeList.ticket.perMonth')}</span>
            </HStack>
          ) : (
            // 쿠폰 미적용: 할인가 직접 표시 (빨간 할인율 + 검정 가격)
            <HStack className="gap-0.5 items-center">
              <span className="text-[18px] font-bold text-red-normal leading-7">{viewDiscountRate}%</span>
              <span className="text-[18px] font-bold text-[#1C1C1C] leading-7">
                {formatKrwAmount(viewPrice, effectiveLocale)}
              </span>
              <span className="text-[14px] text-[#1C1C1C]">{t('subscribeList.ticket.perMonth')}</span>
            </HStack>
          )}
        </VStack>

        {/* 최대 혜택 적용가 박스 (쿠폰 적용 시에만 표시) */}
        {hasCouponApplied && (
          <div className="flex flex-col rounded-lg p-3" style={{ backgroundColor: '#F5F5F5' }}>
            <Flex
              className={`justify-between items-center ${hasDiscountDetails ? 'cursor-pointer' : ''}`}
              onClick={() => hasDiscountDetails && setIsExpanded(!isExpanded)}
            >
              <VStack className="gap-0.5">
                <span className="text-[12px] text-gray-400 tracking-[-0.36px]">
                  {t('subscribeList.paymentOption.bestBenefitPrice')}
                </span>
                <HStack className="gap-0.5 items-center">
                  <span className="text-[18px] font-bold text-red-normal leading-7">{finalDiscountRate}%</span>
                  <span className="text-[18px] font-bold text-[#1C1C1C] leading-7">
                    {formatKrwAmount(finalPrice, effectiveLocale)}
                  </span>
                  <span className="text-[14px] text-[#1C1C1C]">{t('subscribeList.ticket.perMonth')}</span>
                </HStack>
              </VStack>
              {hasDiscountDetails && (
                <ChevronDownIcon
                  width={24}
                  height={24}
                  className="text-[#1C1C1C] transition-transform duration-200"
                  style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              )}
            </Flex>

            {/* 할인 상세 내역 (펼침 시) */}
            {isExpanded && hasDiscountDetails && (
              <div className="flex flex-col gap-3 mt-3">
                <div className="w-full h-px bg-[#E8E8E8]" />
                <div className="flex flex-col w-full">
                  {discountDetails.map((item, index) => (
                    <Flex key={index} className="justify-between items-center w-full">
                      <span className="text-[14px] text-gray-400 tracking-[-0.42px] leading-[22px]">{item.label}</span>
                      <span className="text-[14px] text-red-normal tracking-[-0.42px] leading-[22px] text-right whitespace-nowrap shrink-0">
                        -{formatKrwAmount(item.amount, effectiveLocale)}
                        {t('subscribeList.ticket.perMonth')}
                      </span>
                    </Flex>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </VStack>

      {/* 구매하기 버튼 */}
      <Button
        className="w-full"
        size="xLarge"
        variant={isLumpSum || isIpad ? 'primary' : 'blue'}
        onClick={onPurchase}
        disabled={isPurchasing}
        data-testid={testId ? `btn-purchase-${type}` : undefined}
        eventParams={{ name: UI_IDS.btn.purchaseType(type), location: 'payment', action: 'submit' }}
      >
        {isPurchasing ? t('subscribeList.paymentOption.movingToPayment') : t('subscribeList.ticket.purchase')}
      </Button>
    </VStack>
  )
}

export default PaymentOptionCard
